import React, { useEffect, useRef, useState } from "react";
import styled, { css, createGlobalStyle } from "styled-components";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cachedGet } from "../api";
import { normalizeMeQuranArabic } from "../normalizeMeQuranArabic";
import { ScrollContainer } from "./landingPageSharedStyledComponents";
import meQuranFont from "../../me_quran_volt_newmet.ttf";

// Lesson.css declares this face, but it is not loaded on the home page.
const MeQuranFontFace = createGlobalStyle`
  @font-face {
    font-family: 'Me Quran';
    src: url(${meQuranFont}) format('truetype');
    font-weight: normal;
    font-style: normal;
    font-display: swap;
  }
`;

const ONE_HOUR_MS = 3600000;

function localDateString(date = new Date()) {
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

const convertToCloudFrontUrl = (s3Url) => {
  if (!s3Url) return s3Url;
  return s3Url.replace(
    "https://hhcourses-assets.s3.us-east-2.amazonaws.com",
    "https://d10jmd9bhq0s4v.cloudfront.net"
  );
};

const clampLines = (lines) => css`
  display: -webkit-box;
  -webkit-line-clamp: ${lines};
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Section = styled.section`
  position: relative;
  margin-top: 40px;
  font-family: "Quicksand", sans-serif;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  margin-left: 2vh;
  padding: 10px 0;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 50px;
    height: 3px;
    background-color: #8747d4;
  }
`;

const EDGE_FADE = (size) => css`
  mask-image: linear-gradient(
    to right,
    transparent 0,
    #000 ${size}px,
    #000 calc(100% - ${size}px),
    transparent 100%
  );
  -webkit-mask-image: linear-gradient(
    to right,
    transparent 0,
    #000 ${size}px,
    #000 calc(100% - ${size}px),
    transparent 100%
  );
`;

const QuoteScroller = styled(ScrollContainer)`
  gap: 16px;
  padding: 16px 16px 22px;
  scroll-padding-left: 16px;
  scroll-padding-right: 16px;
  ${EDGE_FADE(16)}

  @media only screen and (min-width: 601px) {
    padding: 16px 28px 24px;
    scroll-padding-left: 28px;
    scroll-padding-right: 28px;
    ${EDGE_FADE(28)}
  }
`;

const ArrowButton = styled.button`
  display: none;

  @media only screen and (min-width: 601px) {
    display: flex;
    position: absolute;
    top: calc(50% + 24px);
    transform: translateY(-50%);
    width: 40px;
    height: 40px;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 50%;
    cursor: pointer;
    z-index: 2;
    color: #333;
    background: rgba(255, 255, 255, 0.85);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transition: background 0.2s ease;

    &:hover {
      background: white;
    }

    &.left {
      left: 0;
    }

    &.right {
      right: 0;
    }
  }
`;

const Card = styled(motion.article)`
  position: relative;
  flex: 0 0 auto;
  width: 82vw;
  max-width: 320px;
  min-height: 360px;
  display: flex;
  flex-direction: column;
  padding: 22px 20px 16px;
  border-radius: 22px;
  overflow: hidden;
  scroll-snap-align: start;

  @media only screen and (min-width: 601px) {
    width: 310px;
  }

  ${({ $kind, $dark }) =>
    $kind === "ayah"
      ? css`
          color: white;
          background: linear-gradient(155deg, #23103f 0%, #45208a 55%, #8747d4 100%);
          box-shadow: 0 12px 28px rgba(69, 32, 138, 0.35);

          &::before {
            content: "";
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            pointer-events: none;
            opacity: 0.12;
            background-image: radial-gradient(circle at 1px 1px, white 1px, transparent 0);
            background-size: 18px 18px;
          }

          &::after {
            content: "";
            position: absolute;
            top: -90px;
            right: -90px;
            width: 220px;
            height: 220px;
            border-radius: 50%;
            pointer-events: none;
            background: radial-gradient(circle, rgba(252, 103, 34, 0.35), transparent 70%);
          }
        `
      : css`
          color: ${$dark ? "#f3e9d8" : "#3b2f1e"};
          background: ${$dark
            ? "linear-gradient(160deg, #2c2520 0%, #1e1a17 100%)"
            : "linear-gradient(160deg, #fffaf1 0%, #fbeed6 100%)"};
          border: 1px solid ${$dark ? "rgba(255, 255, 255, 0.08)" : "#f1e0bf"};
          box-shadow: 0 12px 28px rgba(120, 80, 20, ${$dark ? 0.3 : 0.14});
        `}
`;

const KindTag = styled.span`
  position: relative;
  z-index: 1;
  align-self: flex-start;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;

  ${({ $kind }) =>
    $kind === "ayah"
      ? css`
          background: rgba(255, 255, 255, 0.16);
          color: white;
        `
      : css`
          background: rgba(252, 103, 34, 0.14);
          color: #c4521b;
        `}
`;

const CardBody = styled.div`
  position: relative;
  z-index: 1;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 14px;
  padding: 16px 0 18px;
`;

const ArabicText = styled.p`
  margin: 0;
  direction: rtl;
  text-align: center;
  font-family: "Me Quran", serif;
  font-size: 1.45rem;
  line-height: 2.05;
  ${clampLines(4)}
`;

const Ornament = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  opacity: 0.55;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: currentColor;
  }

  span {
    width: 6px;
    height: 6px;
    transform: rotate(45deg);
    background: currentColor;
  }
`;

const Translation = styled.p`
  margin: 0;
  text-align: center;
  font-size: 0.93rem;
  line-height: 1.55;
  opacity: 0.92;
  ${clampLines(6)}
`;

const QuoteMark = styled.div`
  position: absolute;
  top: 10px;
  right: 16px;
  font-family: Georgia, serif;
  font-size: 7rem;
  line-height: 1;
  color: #fc6722;
  opacity: 0.18;
  pointer-events: none;
  user-select: none;
`;

const Attribution = styled.p`
  margin: 0;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${({ $dark }) => ($dark ? "#f0a274" : "#b8551f")};
  ${clampLines(2)}
`;

const HadithText = styled.p`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  line-height: 1.6;
  ${clampLines(9)}
`;

const Citation = styled.span`
  position: relative;
  z-index: 1;
  align-self: ${({ $kind }) => ($kind === "ayah" ? "center" : "flex-start")};
  max-width: 100%;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 0.75rem;
  ${clampLines(2)}

  ${({ $kind, $dark }) =>
    $kind === "ayah"
      ? css`
          background: rgba(255, 255, 255, 0.12);
          color: rgba(255, 255, 255, 0.85);
          text-align: center;
        `
      : css`
          padding: 0;
          opacity: 0.65;
          color: ${$dark ? "#f3e9d8" : "#3b2f1e"};
        `}
`;

const CourseChip = styled(Link)`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: auto;
  padding: 8px 10px 8px 8px;
  border-radius: 14px;
  text-decoration: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    text-decoration: none;
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.24);
  }

  ${({ $onDark }) => {
    const color = $onDark ? "#2a1450" : "#ffffff";
    return css`
      background: ${$onDark ? "#ffffff" : "#6f35b8"};

      /* Doubled selector so global link styles from Bootstrap cannot recolour the text. */
      &&,
      &&:hover,
      &&:visited,
      && small,
      && strong,
      && svg {
        color: ${color};
      }
    `;
  }}
`;

const ChipPoster = styled.img`
  width: 30px;
  height: ${30 * (2250 / 1410)}px;
  flex-shrink: 0;
  object-fit: cover;
  border-radius: 5px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
`;

const ChipText = styled.div`
  flex: 1;
  min-width: 0;

  small {
    display: block;
    font-size: 0.66rem;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    font-weight: 700;
    opacity: 0.85;
  }

  strong {
    display: block;
    font-size: 0.9rem;
    font-weight: 700;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

function QuoteCard({ quote, index, isDarkMode }) {
  const { kind } = quote;
  const courseName = quote.courseCardTitle || quote.courseTitle;

  return (
    <Card
      $kind={kind}
      $dark={isDarkMode}
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: index * 0.07, ease: "easeOut" }}
      whileHover={{ y: -4 }}
    >
      <KindTag $kind={kind}>{kind === "ayah" ? "Ayah" : "Hadith & Wisdom"}</KindTag>

      {kind === "ayah" ? (
        <CardBody>
          <ArabicText lang="ar">{normalizeMeQuranArabic(quote.arabic)}</ArabicText>
          <Ornament>
            <span />
          </Ornament>
          <Translation>{quote.translation}</Translation>
          {quote.citation && <Citation $kind={kind}>{quote.citation}</Citation>}
        </CardBody>
      ) : (
        <>
          <QuoteMark aria-hidden="true">&ldquo;</QuoteMark>
          <CardBody>
            {quote.attribution && (
              <Attribution $dark={isDarkMode}>{quote.attribution}</Attribution>
            )}
            <HadithText>{quote.text}</HadithText>
            {quote.citation && (
              <Citation $kind={kind} $dark={isDarkMode}>
                {quote.citation}
              </Citation>
            )}
          </CardBody>
        </>
      )}

      <CourseChip
        to={`/${quote.courseTitle.replace(/\s/g, "")}`}
        $onDark={kind === "ayah" || isDarkMode}
      >
        {quote.courseImage && (
          <ChipPoster
            src={convertToCloudFrontUrl(quote.courseImage)}
            alt=""
            loading="lazy"
            decoding="async"
          />
        )}
        <ChipText>
          <small>From the course</small>
          <strong>{courseName}</strong>
        </ChipText>
        <ChevronRight size={18} />
      </CourseChip>
    </Card>
  );
}

function QuotesOfTheDay({ isDarkMode }) {
  const [quotes, setQuotes] = useState([]);
  const [canScroll, setCanScroll] = useState(false);
  const scrollerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    cachedGet(`/quotesOfTheDay?date=${localDateString()}`, { ttl: ONE_HOUR_MS })
      .then((data) => {
        if (!cancelled) setQuotes(Array.isArray(data?.quotes) ? data.quotes : []);
      })
      .catch((error) => {
        console.error("Error fetching quotes of the day:", error);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const check = () => {
      const el = scrollerRef.current;
      setCanScroll(Boolean(el && el.scrollWidth > el.clientWidth));
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [quotes]);

  if (quotes.length === 0) return null;

  const scroll = (direction) => {
    scrollerRef.current?.scrollBy({
      left: direction === "left" ? -330 : 330,
      behavior: "smooth",
    });
  };

  return (
    <Section>
      <MeQuranFontFace />
      <SectionTitle className={isDarkMode ? "darkThemeFont" : "lightThemeFont"}>
        Quotes of the Day
      </SectionTitle>

      {canScroll && (
        <ArrowButton className="left" onClick={() => scroll("left")} aria-label="Previous quotes">
          <ChevronLeft size={22} />
        </ArrowButton>
      )}
      <QuoteScroller ref={scrollerRef}>
        {quotes.map((quote, index) => (
          <QuoteCard
            key={`${quote.courseId}-${index}`}
            quote={quote}
            index={index}
            isDarkMode={isDarkMode}
          />
        ))}
      </QuoteScroller>
      {canScroll && (
        <ArrowButton className="right" onClick={() => scroll("right")} aria-label="More quotes">
          <ChevronRight size={22} />
        </ArrowButton>
      )}
    </Section>
  );
}

export default QuotesOfTheDay;
